/**
 * ChildFormsGridPanel Component
 * Grid editor for managing child forms in the FormContainer
 */

import React, { useCallback, useState } from 'react';
import { ChildFormConfig, DataBindingConfig } from '../types/index';
import { FormBrowseModal, FormBrowseItem } from './FormBrowseModal';

export interface ChildFormsGridPanelProps {
  control: any; // FormControl type
  onChange: (control: any) => void;
  onSelectChild: (index: number) => void;
  selectedIndex: number;
}

/**
 * ChildFormsGridPanel
 * Allows adding, removing, reordering, and editing child forms
 */
export const ChildFormsGridPanel: React.FC<ChildFormsGridPanelProps> = ({
  control,
  onChange,
  onSelectChild,
  selectedIndex,
}) => {
  const childForms = control.config?.childForms || [];
  const [browseOpen, setBrowseOpen] = useState(false);

  const addFormsFromBrowse = useCallback((forms: FormBrowseItem[]) => {
    const newChildren: ChildFormConfig[] = forms.map((f, i) => ({
      formID: String(f.id),
      label: f.name,
      order: (childForms.length || 0) + i + 1,
      dataBinding: {
        mode: 'shared-root',
        syncStrategy: 'debounced',
        saveTarget: 'parent',
        debounceMs: 500,
      } as DataBindingConfig,
      associationName: '',
      sequenceName: '',
      sectionName: '',
      displayName: '',
      description: '',
    }));
    onChange({
      ...control,
      config: { ...control.config, childForms: [...childForms, ...newChildren] },
    });
  }, [childForms, control, onChange]);

  const addChildForm = useCallback(() => {
    const newChild: ChildFormConfig = {
      formID: `form-${Date.now()}`,
      label: 'New Form',
      order: (childForms.length || 0) + 1,
      dataBinding: {
        mode: 'shared-root',
        syncStrategy: 'debounced',
        saveTarget: 'parent',
        debounceMs: 500,
      } as DataBindingConfig,
      associationName: '',
      sequenceName: '',
      sectionName: '',
      displayName: '',
      description: '',
    };

    onChange({
      ...control,
      config: { ...control.config, childForms: [...childForms, newChild] },
    });
  }, [childForms, control, onChange]);

  const removeChildForm = useCallback(
    (index: number) => {
      onChange({
        ...control,
        config: {
          ...control.config,
          childForms: childForms.filter((_: any, i: number) => i !== index),
        },
      });
      if (selectedIndex >= childForms.length - 1 && selectedIndex > 0) {
        onSelectChild(selectedIndex - 1);
      }
    },
    [childForms, control, onChange, selectedIndex, onSelectChild]
  );

  const updateChildForm = useCallback(
    (index: number, field: string, value: any) => {
      onChange({
        ...control,
        config: {
          ...control.config,
          childForms: childForms.map((child: any, i: number) =>
            i === index ? { ...child, [field]: value } : child
          ),
        },
      });
    },
    [childForms, control, onChange]
  );

  const moveChildForm = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= childForms.length) return;

      const newForms = [...childForms];
      [newForms[index], newForms[newIndex]] = [newForms[newIndex], newForms[index]];
      newForms.forEach((child: any, i: number) => { child.order = i + 1; });

      onChange({
        ...control,
        config: { ...control.config, childForms: newForms },
      });
      onSelectChild(newIndex);
    },
    [childForms, control, onChange, onSelectChild]
  );

  const COLUMNS = [
    { label: 'Form ID',      width: 100 },
    { label: 'Label',        width: 80  },
    { label: 'Order',        width: 50  },
    { label: 'Binding',      width: 100 },
    { label: 'Association',  width: 120 },
    { label: 'Sequence',     width: 100 },
    { label: 'Section',      width: 100 },
    { label: 'Display Name', width: 120 },
    { label: 'Description',  width: 150 },
    { label: 'Actions',      width: 70  },
  ];

  if (childForms.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div className="af-child-forms-grid__empty">
          <p style={{ margin: '0 0 12px 0' }}>No child forms yet. Add one to get started.</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="af-child-forms-grid__add-btn" onClick={addChildForm}>
              + Add Manually
            </button>
            <button className="af-child-forms-grid__add-btn" onClick={() => setBrowseOpen(true)}>
              Browse Forms...
            </button>
          </div>
          <FormBrowseModal
            open={browseOpen}
            onClose={() => setBrowseOpen(false)}
            existingFormIds={[]}
            onSelect={addFormsFromBrowse}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', overflow: 'auto', display: 'block' }}>
        <thead style={{ display: 'block' }}>
          <tr style={{ display: 'flex', gap: '2px' }}>
            {COLUMNS.map((col) => (
              <th
                key={col.label}
                className="af-child-forms-grid__header-cell"
                style={{ flex: `0 0 ${col.width}px` }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ display: 'block', maxHeight: '400px', overflowY: 'auto' }}>
          {childForms.map((child: ChildFormConfig, index: number) => {
            const isSelected = index === selectedIndex;
            const cellClass = `af-child-forms-grid__cell${isSelected ? ' af-child-forms-grid__cell--selected' : ''}`;

            return (
              <tr
                key={child.formID}
                className="af-child-forms-grid__row"
                onClick={() => onSelectChild(index)}
                style={{ cursor: 'pointer', display: 'flex', gap: '2px' }}
              >
                <td className={cellClass} style={{ flex: '0 0 100px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="text"
                    value={child.formID}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'formID', e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={cellClass} style={{ flex: '0 0 80px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="text"
                    value={child.label}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'label', e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={cellClass} style={{ flex: '0 0 50px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="number"
                    style={{ width: '40px' }}
                    value={child.order || index + 1}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'order', parseInt(e.target.value)); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={cellClass} style={{ flex: '0 0 100px' }}>
                  <select
                    className="af-child-forms-grid__select"
                    value={child.dataBinding.mode}
                    onChange={(e) => {
                      e.stopPropagation();
                      updateChildForm(index, 'dataBinding', { ...child.dataBinding, mode: e.target.value as any });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="shared-root">Shared</option>
                    <option value="sub-root">Sub</option>
                    <option value="independent">Indep.</option>
                  </select>
                </td>
                <td className={cellClass} style={{ flex: '0 0 120px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="text"
                    placeholder="e.g., User-Address"
                    value={child.associationName || ''}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'associationName', e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={cellClass} style={{ flex: '0 0 100px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="text"
                    placeholder="e.g., Step 1"
                    value={child.sequenceName || ''}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'sequenceName', e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={cellClass} style={{ flex: '0 0 100px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="text"
                    placeholder="e.g., Personal"
                    value={child.sectionName || ''}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'sectionName', e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={cellClass} style={{ flex: '0 0 120px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="text"
                    placeholder="UI label"
                    value={child.displayName || ''}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'displayName', e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={cellClass} style={{ flex: '0 0 150px' }}>
                  <input
                    className="af-child-forms-grid__input"
                    type="text"
                    placeholder="Describe purpose"
                    value={child.description || ''}
                    onChange={(e) => { e.stopPropagation(); updateChildForm(index, 'description', e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td
                  className={cellClass}
                  style={{ flex: '0 0 70px', display: 'flex', gap: '2px', flexWrap: 'wrap', alignContent: 'center' }}
                >
                  <button
                    className="af-child-forms-grid__button"
                    onClick={(e) => { e.stopPropagation(); moveChildForm(index, 'up'); }}
                    disabled={index === 0}
                    title="Move up"
                  >↑</button>
                  <button
                    className="af-child-forms-grid__button"
                    onClick={(e) => { e.stopPropagation(); moveChildForm(index, 'down'); }}
                    disabled={index === childForms.length - 1}
                    title="Move down"
                  >↓</button>
                  <button
                    className="af-child-forms-grid__delete-btn"
                    onClick={(e) => { e.stopPropagation(); removeChildForm(index); }}
                    title="Delete"
                  >✕</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button className="af-child-forms-grid__add-btn" onClick={addChildForm}>
          + Add Manually
        </button>
        <button className="af-child-forms-grid__add-btn" onClick={() => setBrowseOpen(true)}>
          Browse Forms...
        </button>
      </div>

      <FormBrowseModal
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        existingFormIds={childForms.map((c: ChildFormConfig) => c.formID)}
        onSelect={addFormsFromBrowse}
      />
    </div>
  );
};

ChildFormsGridPanel.displayName = 'ChildFormsGridPanel';
